"use client";

// The deposit wizard's state channel, and the reason a LIVE dialog<->drawer
// switch can keep every bit of that state.
//
// Why a store instead of a plain context value: the wizard state must live
// ABOVE `ResponsiveShell` (swapping radix `Dialog.Root` <-> vaul `Drawer.Root`
// unmounts everything below it, portal content included), yet it must also be
// MOUNTED ONLY WHILE THE MODAL IS OPEN -- that is what keeps "no fetch before
// open" and "reset on close" true without a single reset effect. A component
// that both wraps the shell and mounts conditionally is impossible: React
// remounts a subtree whose parent element type changes, so a conditional
// wrapper would tear the shell (and the host's `<RelayDeposit.Trigger>`) down
// and back up on every open/close -- losing vaul's slide-out animation and
// radix's focus-return-to-trigger.
//
// So the ALWAYS-mounted `RelayWizardStateHost` owns this store and renders the
// state-owning engine as a SIBLING of the shell, mounted only while open. The
// engine publishes its bundle into the store; `RelayDepositBody`, arbitrarily
// deep inside whichever portal is currently mounted, subscribes. Sideways data
// flow is exactly what a context value cannot do -- the provider sits above the
// engine, so the engine can never change it.
import {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

import type { DepositState } from "../hooks/useDepositAddress";
import type { Catalog, OrderSummary, OriginOption, QuoteResult, Target } from "../lib/types";
import type { RecoveryTargetEntry } from "../lib/recoveryGroups";
import type { RelayBalance, RelayWalletProps } from "../types";

export type DepositArrivalPhase =
  | "connecting"
  | "waiting"
  | "detected"
  | "funded"
  | "processing"
  | "completed"
  | "failed"
  | "unavailable";

export type RelayDepositView = "deposit" | "history" | "recover";

/** Live projection of the current reusable forwarder's funding/delivery state.
 * SSE is only an invalidation signal; RelayWizardEngine derives this after
 * re-fetching /orders and, for the actual waiting balance, /recovery. */
export interface DepositArrivalStatus {
  phase: DepositArrivalPhase;
  /** Latest newly-attributed BSC-forwarder arrival, formatted in bridge-token
   * human units. It is not the amount originally sent on the origin chain. */
  amount?: string;
  symbol?: string;
  backendStatus?: string;
}

/**
 * Everything `RelayDepositBody` renders from — the whole of what used to be
 * computed inside the portal. Kept deliberately flat and pre-derived (every
 * `show*`/`can*` gate resolved) so the body stays render-only: no hook of its
 * own beyond the shell context (theme/isMobile/close) and this bundle.
 *
 * No step/navigation fields any more: the deposit body is ONE screen as of the
 * 2026-07-31 decision (see DepositPanel.tsx). `step`/`goToSource`/`goToExecute`
 * and the `canContinue` gate left with the Continue button; the only survivor of
 * that gate is `amountHint`, which now merely explains itself inline.
 */
export interface RelayWizardBundle {
  // ---- Deposit modal tabs -----------------------------------------------
  activeView: RelayDepositView;
  setActiveView: Dispatch<SetStateAction<RelayDepositView>>;
  /** Compatibility projection used by the recovery polling path. */
  recoveryOpen: boolean;
  setRecoveryOpen: Dispatch<SetStateAction<boolean>>;

  // ---- Resolved configuration (shared by RecoverFunds + the fetches) ----
  resolvedApiBaseUrl: string;
  wallet: RelayWalletProps;

  // ---- useDepositAddress ------------------------------------------------
  catalog?: Catalog;
  catalogLoading: boolean;
  catalogError?: Error;
  origin?: OriginOption;
  setOrigin: (origin: OriginOption) => void;
  amount: string;
  setAmount: (value: string) => void;
  recipient: string;
  setRecipient: (value: string) => void;
  recipientValid: boolean;
  balance?: RelayBalance & { formatted: string };
  balanceLoading: boolean;
  insufficientBalance: boolean;
  depositAddress?: string;
  depositAddressLoading: boolean;
  depositAddressError?: Error;
  /** SSE + fallback-poll projection for the address currently on screen. */
  arrivalStatus: DepositArrivalStatus;
  quote?: QuoteResult;
  quoteLoading: boolean;
  quoteError?: Error;
  /** The hook's own gate, unchanged -- the body still ANDs it with `showOneClick`. */
  canDeposit: boolean;
  deposit: () => Promise<void>;
  depositState: DepositState;

  // ---- useTargetTokens --------------------------------------------------
  targetOptions: Target[];
  selectedTarget?: Target;
  setSelectedTarget: (symbol: string) => void;

  // ---- Pre-derived render gates ----------------------------------------
  showAmountInput: boolean;
  /** Prominent minimum-deposit guidance shown when the amount input is hidden
   * (`hideAmount`) -- the meta line under the input can't carry it then. */
  minNotice?: {
    min: string;
    symbol: string;
    /** Human amount already sitting un-swept at the user's forwarder from an
     * earlier below-minimum deposit, in the RECOVERY token's decimals. Set only
     * together with `remaining` (i.e. only when it doesn't yet clear `min`). */
    pending?: string;
    /** Symbol for `pending`, when the backend reports one -- omitted rather
     * than guessed (`RecoveryInfo` has no top-level symbol field). */
    pendingSymbol?: string;
    /** What the NEXT deposit still has to cover: `min` minus `pending`, both
     * rescaled to the origin token's decimals. Presence is what switches the
     * notice to its "accumulating" copy. */
    remaining?: string;
  };
  showRecipientInput: boolean;
  showTargetSelector: boolean;
  showOneClick: boolean;
  showConnectPrompt: boolean;
  showRecipientHint: boolean;
  canRecover: boolean;
  recoverableCount: number;
  recoveryTargetsLoading: boolean;
  /** Recovery info per catalog target (successful fetches only), in catalog
   * order -- <RecoverFunds>' per-forwarder mode input (upstream e5bff75).
   * Empty when the catalog has no targets: the panel then self-fetches. */
  recoveryTargets: RecoveryTargetEntry[];
  /** The user's orders, shared by History, the live deposit monitor, and the
   * recovery panel. There is exactly one orders subscription per open modal. */
  recoveryOrders: OrderSummary[];
  historyOrdersLoading: boolean;
  historyOrdersError?: Error;
  /** Refetches every target's recovery info (bumped after a recovery
   * attempt changes on-chain state). */
  refreshRecovery: () => void;
  /** The effective trusted-factory allowlist: the host's explicit prop, else
   * the CDN apps.json `relay.forwarderFactories` values (which replace the pinned
   * defaults when valid -- see mergeTrustedFactories). Threaded to
   * <RecoverFunds> so the one-click deposit and the recovery panel verify
   * against the same list. */
  trustedFactories?: readonly string[];
  /** Inline advisory under the amount input: the entered amount is below the
   * effective minimum or above the ops-set maximum. Gates NOTHING -- the deposit
   * address is minted from (recipient, origin, target) alone, and the address is
   * reusable, so an out-of-range amount is a hint about what to send, not a
   * blocker. (This is what `canContinue`/`continueBlockedHint` collapsed into
   * when the Continue button went away.) */
  amountHint?: string;
  amountSmallestUnits?: string;
  /** "Max" affordance, or undefined when there is no spendable balance to fill in. */
  onMax?: () => void;
}

type Listener = () => void;

export interface RelayWizardStore {
  subscribe(listener: Listener): () => void;
  /** The currently published bundle, or null while the modal is closed. */
  getSnapshot(): RelayWizardBundle | null;
  publish(next: RelayWizardBundle | null): void;
}

export function createRelayWizardStore(): RelayWizardStore {
  let snapshot: RelayWizardBundle | null = null;
  const listeners = new Set<Listener>();

  return {
    getSnapshot: () => snapshot,
    publish(next) {
      if (next === snapshot) return;
      snapshot = next;
      // Copied before iterating: a listener that unsubscribes while being
      // notified (a body unmounting on the same commit) must not skip the rest.
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export const RelayWizardStoreContext = createContext<RelayWizardStore | null>(null);

export function useRelayWizardStore(): RelayWizardStore {
  const store = useContext(RelayWizardStoreContext);
  if (!store) {
    throw new Error("RelayDeposit compound components must be used within <RelayDeposit>");
  }
  return store;
}

/**
 * Subscribes to the published wizard bundle. Null means "nothing published
 * yet", which for `RelayDepositBody` only ever happens on a closed modal (the
 * engine publishes in a layout effect that runs BEFORE this one — sibling
 * fibers complete in order — so the very first body render inside a freshly
 * opened portal already has a bundle by the time it commits; a body remounted
 * by a dialog<->drawer swap reads the live snapshot straight out of `useState`).
 *
 * Deliberately not `useSyncExternalStore`: that one subscribes in a PASSIVE
 * effect, so a bundle published in the engine's layout effect would only reach
 * the body after paint — one visibly empty frame on every open.
 */
export function useRelayWizardBundle(): RelayWizardBundle | null {
  const store = useRelayWizardStore();
  const [bundle, setBundle] = useState(store.getSnapshot);

  useLayoutEffect(() => {
    // Catches a publish that landed between this component's render and this
    // effect (i.e. the engine's own layout effect, on the first open).
    setBundle(store.getSnapshot());
    return store.subscribe(() => setBundle(store.getSnapshot()));
  }, [store]);

  return bundle;
}
